import React, { useEffect, useMemo, useRef, useState } from "react";
import { Timeline } from "@xzdarcy/react-timeline-editor";
import { Button, Tooltip, Typography } from "@mui/material";
import { SSENSE_URL } from "../../config/stepoutAPI";
import axios from "axios";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import { CustomActionRender } from "./CustomRender";

// Function to parse a single event string
const parseEventString = (eventStr) => {
  const parts = eventStr.split("-");
  const rawTime = parts[6]; // e.g., "1:25:41"
  if (!rawTime || !rawTime.includes(":")) return null;

  const [hh, mm, ss] = rawTime.split(":").map(Number);
  const start = hh * 3600 + mm * 60 + ss;

  const effectId = parts[2] || "default"; // LP, AD, etc.

  return {
    id: eventStr,
    start,
    end: start + 0.5,
    effectId,
  };
};

const parseTimestamp = (timestamp) => {
  if (!timestamp || typeof timestamp !== "string" || !timestamp.includes(":")) {
    console.warn("Invalid timestamp:", timestamp);
    return 0;
  }

  const [hh, mm, ssMs] = timestamp.split(":");
  if (!ssMs) return 0;

  const [ss, ms = "0"] = ssMs.split(".");
  return (
    parseInt(hh || "0") * 3600 * 1000 +
    parseInt(mm || "0") * 60 * 1000 +
    parseInt(ss || "0") * 1000 +
    parseInt(ms || "0")
  );
};

const parseAllEvents = (rawString) => {
  const entries = rawString.split(",").filter(Boolean);
  const events = [];

  let i = 0;
  while (i < entries.length) {
    const rawCurrent = entries[i];
    const rawNext = entries[i + 1];
    const current = rawCurrent?.split("-");
    const next = rawNext?.split("-");

    if (!current || current.length < 7) {
      i += 1;
      continue;
    }

    const currCode = current[2] || "";
    const nextCode = next?.[2] || "";

    const currTime = parseTimestamp(current[6]);
    let endTime;
    let isPaired = false;

    if (
      next &&
      next.length >= 7 &&
      nextCode.startsWith("X") &&
      nextCode.substring(1) === currCode
    ) {
      endTime = parseTimestamp(next[6]);
      isPaired = true;
    } else {
      endTime = currTime + 1000;
    }

    const id = `evt-${i}-${currCode}`;

    events.push({
      id,
      start: currTime / 1000,
      end: currTime === endTime ? (endTime + 1000) / 1000 : endTime / 1000,
      effectId: "event-marker",
      label: currCode,
      raw: isPaired ? `${rawCurrent},${rawNext}` : rawCurrent,
    });

    i += isPaired ? 2 : 1;
  }

  return events;
};

// React Query fetcher
const fetchMatchVideoString = async ({ queryKey }) => {
  const [_key, { match_id, analyst_id, token }] = queryKey;

  const { data } = await axios.post(
    `${SSENSE_URL}get_match_video_string/v2/`,
    { match_id, analyst_id },
    {
      headers: {
        Authorization: "ANALYST " + token,
      },
    }
  );

  return data; // should be string or { event_string: "..." }
};

const VideoTimelineEditor = ({
  durationMs,
  videoTimeMs,
  onSeek,
  setTime,
  setIsPlaying,
  isPlaying,
  timelineState,
  setShowTimeline,
  showTimeline,
  matchId,
  analystId,
}) => {
  const durationSec = useMemo(() => durationMs / 1000, [durationMs]);
  const videoTimeSec = useMemo(() => videoTimeMs / 1000, [videoTimeMs]);

  const playerPanel = useRef();
  const autoScrollWhenPlay = useRef(true);

  const [timelineHeight, setTimelineHeight] = useState(100); // default height
  const resizingRef = useRef(false);

  const startResizing = () => {
    resizingRef.current = true;
  };

  const stopResizing = () => {
    resizingRef.current = false;
  };

  const handleMouseMove = (e) => {
    if (resizingRef.current) {
      const newHeight =
        e.clientY - containerRef.current.getBoundingClientRect().top;
      if (newHeight > 50 && newHeight < 500) {
        setTimelineHeight(newHeight);
      }
    }
  };
  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopResizing);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopResizing);
    };
  }, []);

  const { token } = useSelector((state) => ({
    token: state.auth.token,
  }));

  const { data, isLoading, isError } = useQuery(
    ["matchVideoString", { match_id: matchId, analyst_id: analystId, token }],
    fetchMatchVideoString,
    {
      enabled: !!matchId && !!analystId && !!token,
    }
  );

  // Generate effects based on event types
  const effects = useMemo(
    () => ({
      cursor: { id: "cursor", name: "" },
      "event-marker": { id: "event-marker", name: "Event" },
    }),
    []
  );

  const assignToTracks = (actions) => {
    const sorted = [...actions].sort((a, b) => a.start - b.start);
    const tracks = [];

    sorted.forEach((action) => {
      let placed = false;

      for (let i = 0; i < tracks.length; i++) {
        const hasOverlap = tracks[i].some(
          (existing) =>
            !(action.end <= existing.start || action.start >= existing.end)
        );

        if (!hasOverlap) {
          tracks[i].push(action);
          placed = true;
          break;
        }
      }

      if (!placed) {
        tracks.push([action]);
      }
    });

    return tracks.map((trackActions, index) => ({
      id: `track-${index + 1}`,
      actions: trackActions,
    }));
  };

  const CHUNK_SIZE = 500;

  // Build editorData from event string
  const editorData = useMemo(() => {
    if (!data) return [];

    const rawString = typeof data === "string" ? data : data?.temp_string;
    if (!rawString) return [];

    const startSec = videoTimeMs / 1000 - 10;
    const endSec = videoTimeMs / 1000 + 10;

    const actions = parseAllEvents(rawString);

    const filteredActions = actions?.filter(
      (a) => a.start < endSec && a.end > startSec
    );

    const limited = filteredActions?.slice(0, CHUNK_SIZE);

    return assignToTracks(limited);
  }, [data, videoTimeMs]);

  const handleTimelineClick = (e) => {
    const timeInMs = e * 1000;
    setTime(timeInMs);
  };

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1000);
  const scale1 = 10;
  const scaleWidth = containerWidth / (durationSec / 60);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    requestAnimationFrame(() => {
      handleResize();
    });

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!timelineState.current) return;
    const engine = timelineState.current;

    engine.listener.on("play", () => setIsPlaying(true));
    engine.listener.on("paused", () => setIsPlaying(false));
    engine.listener.on("afterSetTime", ({ time }) => setTime(time));
    engine.listener.on("setTimeByTick", ({ time }) => {
      setTime(time);
      if (autoScrollWhenPlay.current) {
        const autoScrollFrom = 500;
        const left = time * (containerWidth / scale1) + 20 - autoScrollFrom;
        timelineState.current.setScrollLeft(left);
      }
    });

    return () => {
      engine.pause();
      engine.listener.offAll();
    };
  }, []);

  const totalWidth = (durationSec / scale1) * scaleWidth;
  const maxScrollLeft = totalWidth - containerWidth;
  const safeStartLeft = Math.min(Math.max(20, 20), maxScrollLeft);

  const [zoomLevel, setZoomLevel] = useState(5);
  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 10;
  const ZOOM_STEP = 0.25;

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + ZOOM_STEP, ZOOM_MAX));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - ZOOM_STEP, ZOOM_MIN));
  };

  const MIN_SCALE_WIDTH = 30;
  const rawScaleWidth = Math.max(containerWidth, 500) / durationSec;
  const scaleW = Math.max(rawScaleWidth, MIN_SCALE_WIDTH) * zoomLevel;

  useEffect(() => {
    if (timelineState.current) {
      const playheadX = (videoTimeMs / 1000) * scaleW;
      const left = Math.max(0, playheadX - containerWidth / 2);
      timelineState.current.setScrollLeft(left);
    }
  }, [videoTimeMs, timelineState.current, containerWidth]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: timelineHeight,
        position: "relative",
        userSelect: resizingRef.current ? "none" : "auto",
        marginBottom: 50,
      }}
    >
      {!isNaN(durationSec) && durationSec > 0 && (
        <div
          ref={containerRef}
          style={{ width: "100%", height: "100%", position: "relative" }}
        >
          <div style={{ marginBottom: 8 }}>
            {showTimeline && (
              <Button
                sx={{ alignSelf: "flex-start", marginRight: 2 }}
                size="small"
                variant="contained"
                onClick={() => setShowTimeline(!showTimeline)}
              >
                Hide timeline
              </Button>
            )}
            <button onClick={handleZoomOut}>➖ Zoom Out</button>
            <button onClick={handleZoomIn} style={{ marginLeft: 8 }}>
              ➕ Zoom In
            </button>
          </div>

          <Timeline
            key={`timeline-${scaleWidth}-${containerWidth}`}
            style={{ width: `${containerWidth}px`, height: "100%" }}
            scaleSplitCount={10}
            onChange={() => {}}
            disableDrag
            scale={1}
            minScaleCount={durationSec}
            maxScaleCount={durationSec}
            scaleWidth={scaleW}
            onCursorDrag={(time) => {
              const clampedTime = Math.min(time, durationSec);
              setTime(clampedTime * 1000);
            }}
            startLeft={safeStartLeft}
            autoScroll={true}
            ref={timelineState}
            editorData={editorData}
            effects={effects}
            onClickTimeArea={handleTimelineClick}
            getActionRender={(action, row) => (
              <CustomActionRender action={action} row={row} />
            )}
            getScaleRender={(scaleIndex) => {
              const totalSeconds = scaleIndex; // if scale = 1 sec per tick
              const h = Math.floor(totalSeconds / 3600);
              const m = Math.floor((totalSeconds % 3600) / 60);
              const s = totalSeconds % 60;

              const pad = (n) => String(n).padStart(2, "0");
              const timeLabel =
                h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;

              return (
                <Typography
                  variant="caption"
                  component="span"
                  sx={{ fontSize: 10, color: "#e1e1e1" }}
                >
                  {timeLabel}
                </Typography>
              );
            }}
          />
        </div>
      )}
      <div
        onMouseDown={startResizing}
        style={{
          position: "absolute",
          bottom: -50,
          left: 0,
          height: 6,
          width: "100%",
          cursor: "ns-resize",
          background: "rgba(0,0,0,0.1)",
          zIndex: 10,
        }}
      />
    </div>
  );
};

export default VideoTimelineEditor;
